"use client";

import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
  type Ref,
} from "react";
import dynamic from "next/dynamic";
import { Download, Loader2, Plus } from "lucide-react";
import { EcomButton as Button } from "../../ui";
import { downloadFile, downloadFiles } from "../../lib/download";
import { IMAGE_ACCEPT } from "../../lib/file-utils";
import { patchTaskOutputImage } from "../../lib/ecom-image-api";
import type { EcomActiveRefineState, EcomRefineTrackInfo } from "../../lib/ecom-tab-history";
import {
  DETAIL_REPLICA_RESULT_LABEL_IMAGE_GAP_PX,
  DETAIL_REPLICA_RESULT_LABEL_WIDTH_PX,
  DETAIL_REPLICA_RESULT_MIN_COLUMN_GAP_PX,
  DETAIL_REPLICA_RESULT_MIN_STACK_WIDTH_PX,
  DETAIL_REPLICA_WINDOW_OVERSCAN,
  findClosestDetailReplicaItemIndex,
  resolveDetailReplicaImageVariantUrl,
  resolveDetailReplicaItemAspectRatio,
  resolveDetailReplicaRevealBehavior,
  resolveDetailReplicaResultCounts,
  resolveDetailReplicaScrollGuardMs,
  resolveDetailReplicaThumbnailSize,
  resolveDetailReplicaWindowRange,
  revealReferenceScrollTop,
  syncPeerScrollTop,
} from "../../lib/ecom-detail-page-replica";
import { replaceEditedImages } from "../../lib/image-edit-save";
import { resolveMediaUrl } from "../../lib/resolve-media-url";
import ImageEditModal, { type ImageEditSaveResult } from "../image-edit/ImageEditModal";
import ImageActionToolbar, { ActionIconButton } from "./ImageActionToolbar";
import ImageEditActionIcon from "./ImageEditActionIcon";
import EcomGeneratingCard from "./EcomGeneratingCard";
import RefineOverlay from "./RefineOverlay";
import { useRefineLifecycle } from "./useRefineLifecycle";
import { useLatestRefs } from "../../hooks/useLatestRefs";

const ImageRefineEditor = dynamic(
  () => import("./ImageRefineEditor"),
  { ssr: false },
);

// 父级选中索引只在滚动停止后提交：滚动持续期间每次滚动事件都会重置该兜底定时器，
// 真正的提交点是浏览器 scrollend（不支持 scrollend 的环境由该定时器静默后补位）。
const PARENT_COMMIT_IDLE_FALLBACK_MS = 400;

// 软跟随：对侧每帧收敛剩余距离的 48%，约 5 帧（~83ms）追平；
// 快速滑动时对侧略微滞后但不回震，滚动停止后平滑跟上同一位置。
const PEER_FOLLOW_EASE = 0.48;
const PEER_FOLLOW_SETTLE_PX = 1;

type DetailPageReplicaResultPanelProps = {
  references: string[];
  images: string[];
  ratios?: string[];
  status: "empty" | "generating" | "completed" | "failed";
  taskProgresses: number[];
  outputCount: number;
  failedIndexes?: number[];
  regeneratingIndexes?: number[];
  generationStartedAt?: number | null;
  taskId?: string;
  batchChildTaskIds?: string[];
  productImage?: string;
  onPreview?: (url: string, index?: number) => void;
  onFavorite?: (url: string) => void;
  onPublish?: (url: string) => void;
  onRegenerateSingle?: (index: number) => void;
  onImagesUpdated?: (images: string[], targetTaskId?: string) => void;
  onHistoryRefresh?: () => void;
  refineOwnerTabId: string;
  onRefineTrack?: (info: EcomRefineTrackInfo) => boolean | void;
  activeRefines?: Record<number, EcomActiveRefineState>;
  refineStateVersion?: number;
  onInsertReferenceFiles?: (files: FileList, insertAfterIndex: number) => void | Promise<void>;
  /** 删除选中屏参考图（Delete/Backspace 触发） */
  onDeleteReference?: (index: number) => void;
  /** 撤回最近一次删除（Ctrl/Meta+Z 触发） */
  onUndoReferenceDeletion?: () => void;
  /** 右侧配置格切换时外部驱动的定位请求，用于联动参考图与效果图滚动 */
  focusRequest?: { index: number; requestID: number } | null;
  /** 受控选中索引，缺省时由组件内部 state 管理，避免配置面板与参考图选中态脱节 */
  activeIndex?: number;
  /** 选中索引变化回调，用于向上同步配置面板与缩略图选中态 */
  onActiveIndexChange?: (index: number) => void;
};

// 删除/撤回快捷键需排除可编辑元素，避免在输入框内按 Delete/Backspace 误删参考图。
function isKeyboardEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)
    || target.isContentEditable
    || Boolean(target.closest('[contenteditable="true"]'));
}

function RefineAiIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 1024 1024" aria-hidden="true" fill="currentColor">
      <path d="M826.368 325.632c0-7.168 2.048-10.24 10.24-10.24h123.904c7.168 0 10.24 2.048 10.24 10.24v621.568c0 7.168-2.048 10.24-10.24 10.24h-122.88c-8.192 0-10.24-4.096-10.24-10.24l-1.024-621.568z m-8.192-178.176c0-50.176 35.84-79.872 79.872-79.872 48.128 0 79.872 32.768 79.872 79.872 0 52.224-33.792 79.872-81.92 79.872-46.08 1.024-77.824-27.648-77.824-79.872zM462.848 584.704C441.344 497.664 389.12 307.2 368.64 215.04h-2.048c-16.384 92.16-58.368 247.808-92.16 369.664h188.416zM243.712 712.704l-62.464 236.544c-2.048 7.168-4.096 8.192-12.288 8.192H54.272c-8.192 0-10.24-2.048-8.192-12.288l224.256-783.36c4.096-13.312 7.168-26.624 8.192-65.536 0-6.144 2.048-8.192 7.168-8.192H450.56c6.144 0 8.192 2.048 10.24 8.192l250.88 849.92c2.048 7.168 0 10.24-7.168 10.24H573.44c-7.168 0-10.24-2.048-12.288-7.168l-65.536-236.544c1.024 1.024-251.904 0-251.904 0z" />
    </svg>
  );
}

export default function DetailPageReplicaResultPanel({
  references,
  images: rawImages,
  ratios = [],
  status,
  taskProgresses,
  outputCount,
  failedIndexes = [],
  regeneratingIndexes = [],
  generationStartedAt = null,
  taskId = "",
  batchChildTaskIds = [],
  productImage: rawProductImage,
  onPreview,
  onFavorite,
  onPublish,
  onRegenerateSingle,
  onImagesUpdated,
  onHistoryRefresh,
  refineOwnerTabId,
  onRefineTrack,
  activeRefines,
  refineStateVersion,
  onInsertReferenceFiles,
  onDeleteReference,
  onUndoReferenceDeletion,
  focusRequest,
  activeIndex: controlledActiveIndex,
  onActiveIndexChange,
}: DetailPageReplicaResultPanelProps) {
  const resolvedReferences = useMemo(
    () => references.map((image) => resolveMediaUrl(image)),
    [references],
  );
  const images = useMemo(
    () => rawImages.map((img) => resolveMediaUrl(img)),
    [rawImages],
  );
  const referencePreviewImages = useMemo(
    () => references.map((image) => resolveDetailReplicaImageVariantUrl(image, "preview")),
    [references],
  );
  const referenceThumbnailImages = useMemo(
    () => references.map((image) => resolveDetailReplicaImageVariantUrl(image, "small")),
    [references],
  );
  const outputPreviewImages = useMemo(
    () => rawImages.map((image) => resolveDetailReplicaImageVariantUrl(image, "preview")),
    [rawImages],
  );
  const outputThumbnailImages = useMemo(
    () => rawImages.map((image) => resolveDetailReplicaImageVariantUrl(image, "small")),
    [rawImages],
  );
  const productImage = useMemo(
    () => resolveMediaUrl(rawProductImage),
    [rawProductImage],
  );

  const [localActiveIndex, setLocalActiveIndex] = useState(controlledActiveIndex ?? 0);
  const [revealTarget, setRevealTarget] = useState<{ index: number; token: number } | null>(null);
  const { referenceCount, outputSlotCount, selectionCount } = useMemo(
    () => resolveDetailReplicaResultCounts({
      referenceCount: resolvedReferences.length,
      imageCount: images.length,
      taskProgressCount: taskProgresses.length,
      outputCount,
    }),
    [images.length, outputCount, resolvedReferences.length, taskProgresses.length],
  );
  const validImages = useMemo(() => images.filter(Boolean), [images]);
  const safeActive = Math.max(0, Math.min(localActiveIndex, Math.max(0, selectionCount - 1)));
  const windowRange = useMemo(
    () => resolveDetailReplicaWindowRange(
      safeActive,
      Math.max(referenceCount, outputSlotCount),
      DETAIL_REPLICA_WINDOW_OVERSCAN,
    ),
    [outputSlotCount, referenceCount, safeActive],
  );
  // 比例解析提升为面板级数组：滚动重渲染时避免两列共 36 项逐个重复 resolve，
  // 同时让 memo 化的列表项拿到稳定的 number prop 而不是每次重算。
  const itemAspectRatios = useMemo(
    () => Array.from(
      { length: Math.max(referenceCount, outputSlotCount) },
      (_, index) => resolveDetailReplicaItemAspectRatio(ratios[index]),
    ),
    [outputSlotCount, ratios, referenceCount],
  );

  const referenceScrollRef = useRef<HTMLDivElement>(null);
  const outputScrollRef = useRef<HTMLDivElement>(null);
  const referenceImageRefs = useRef<Array<HTMLElement | null>>([]);
  const outputImageRefs = useRef<Array<HTMLElement | null>>([]);
  const referenceItemCentersRef = useRef<number[]>([]);
  const outputItemCentersRef = useRef<number[]>([]);
  const geometryRefreshRafRef = useRef<number | null>(null);
  const referenceThumbRailRef = useRef<HTMLDivElement>(null);
  const outputThumbRailRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const resultRootRef = useRef<HTMLDivElement>(null);
  const revealTokenRef = useRef(0);
  const programmaticScrollUntilRef = useRef(0);
  // 显式定位以用户选择为准：scrollend 之后冷图仍可能加载并调整 scrollTop，
  // 这些布局滚动不能把短末屏改选成视口中央的前一屏。真实用户输入立即接管。
  const explicitSelectionRef = useRef(false);
  useEffect(() => { explicitSelectionRef.current = false; }, [taskId]);
  // 双列软跟随联动状态（替代旧的一帧 guard 硬写对侧 scrollTop）：
  // - userDrivenColumnRef：当前用户正在驱动哪一列，只有该列的滚动事件参与联动计算，
  //   被我们软跟随写入的对侧事件一律忽略，彻底消除双向纠正抖动；
  // - peerFollowRef：对侧渐进追赶目标位置的 rAF 动画状态；
  // - lastPeerWriteRef：最后一次程序化写入记录，用于区分「自写回声」与「用户键盘滚动」。
  const userDrivenColumnRef = useRef<"reference" | "output" | null>(null);
  const peerFollowRef = useRef<{ follower: "reference" | "output"; targetTop: number; raf: number | null } | null>(null);
  const lastPeerWriteRef = useRef<{ column: "reference" | "output"; top: number } | null>(null);
  // 自然比例按预览资源身份记忆；重排复用同图，换图不复用旧槽位尺寸。
  // 只保留当前两列的 URL，不预加载窗口外图片，也不累计已关闭历史任务。
  const measuredReferenceAspectsRef = useRef(new Map<string, number>());
  const measuredOutputAspectsRef = useRef(new Map<string, number>());
  const currentPreviewUrlsRef = useRef({ reference: referencePreviewImages, output: outputPreviewImages });
  currentPreviewUrlsRef.current = { reference: referencePreviewImages, output: outputPreviewImages };
  useLayoutEffect(() => {
    for (const [cache, urls] of [
      [measuredReferenceAspectsRef.current, referencePreviewImages],
      [measuredOutputAspectsRef.current, outputPreviewImages],
    ] as const) {
      const currentUrls = new Set(urls);
      for (const url of cache.keys()) if (!currentUrls.has(url)) cache.delete(url);
    }
  }, [referencePreviewImages, outputPreviewImages]);
  // 版本号 state 仅用于在记录新比例后触发一次重渲染让已测量值生效；
  // map 每次渲染都会重读上面的 ref，因此取值位留空，不需要读取版本号本身。
  const [, setMeasuredAspectVersion] = useState(0);
  const pendingLinkedScrollSourceRef = useRef<"reference" | "output" | null>(null);
  const linkedScrollRafRef = useRef<number | null>(null);
  const parentActiveIndexTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingParentActiveIndexRef = useRef<number | null>(null);
  const referenceCountRef = useRef(referenceCount);
  referenceCountRef.current = referenceCount;
  const outputSlotCountRef = useRef(outputSlotCount);
  outputSlotCountRef.current = outputSlotCount;

  const [referenceThumbSize, setReferenceThumbSize] = useState(() => (
    resolveDetailReplicaThumbnailSize(Number.POSITIVE_INFINITY, 0)
  ));
  const [outputThumbSize, setOutputThumbSize] = useState(() => (
    resolveDetailReplicaThumbnailSize(Number.POSITIVE_INFINITY, 0)
  ));
  const [canvasBadgeLeft, setCanvasBadgeLeft] = useState<number | null>(null);
  const [downloadButtonLeft, setDownloadButtonLeft] = useState<number | null>(null);

  // Refine / color-edit (对齐主图复刻 PreviewArea / ImageStitchPanel)
  const [refineEditorOpen, setRefineEditorOpen] = useState(false);
  const [refineImageUrl, setRefineImageUrl] = useState("");
  const [refineImageIndex, setRefineImageIndex] = useState(0);
  const [imageEditOpen, setImageEditOpen] = useState(false);
  const [imageEditInitialId, setImageEditInitialId] = useState("");
  const [toastMessage, setToastMessage] = useState<{ text: string; type: "success" | "error" } | null>(null);

  // 参考图「插入图片」展开态与拖拽态，与工作区 DetailPageReplicaWorkspace 保持一致：
  // 选中屏点击「插入图片」在两屏之间展开虚线上传框，支持点击与拖拽上传。
  const [insertSlotIndex, setInsertSlotIndex] = useState<number | null>(null);
  const [insertDragOverIndex, setInsertDragOverIndex] = useState<number | null>(null);
  const [insertUploading, setInsertUploading] = useState(false);
  const insertPlaceholderRef = useRef<HTMLLabelElement>(null);
  const onInsertReferenceFilesRef = useRef(onInsertReferenceFiles);
  useEffect(() => { onInsertReferenceFilesRef.current = onInsertReferenceFiles; }, [onInsertReferenceFiles]);
  // 删除/撤回参考图回调，键盘快捷键触发；用 ref 规避闭包过期。
  const onDeleteReferenceRef = useRef(onDeleteReference);
  useEffect(() => { onDeleteReferenceRef.current = onDeleteReference; }, [onDeleteReference]);
  const onUndoReferenceDeletionRef = useRef(onUndoReferenceDeletion);
  useEffect(() => { onUndoReferenceDeletionRef.current = onUndoReferenceDeletion; }, [onUndoReferenceDeletion]);
  const activeIndexRef = useRef(safeActive);
  activeIndexRef.current = safeActive;
  const onActiveIndexChangeRef = useRef(onActiveIndexChange);
  useEffect(() => { onActiveIndexChangeRef.current = onActiveIndexChange; }, [onActiveIndexChange]);
  // page.tsx（巨型父组件）传入的预览/收藏/发布/单屏重试回调引用不稳定，
  // 依照本文件已有 ref 模式包装成稳定函数，再传给 memo 化的主列表项，
  // 避免父级偶发重渲染让 36 个列表项的 memo 比较全部失效。
  const onPreviewRef = useRef(onPreview);
  useEffect(() => { onPreviewRef.current = onPreview; }, [onPreview]);
  const onFavoriteRef = useRef(onFavorite);
  useEffect(() => { onFavoriteRef.current = onFavorite; }, [onFavorite]);
  const onPublishRef = useRef(onPublish);
  useEffect(() => { onPublishRef.current = onPublish; }, [onPublish]);
  const onRegenerateSingleRef = useRef(onRegenerateSingle);
  useEffect(() => { onRegenerateSingleRef.current = onRegenerateSingle; }, [onRegenerateSingle]);
  const previewStackImage = useCallback((url: string, index: number) => {
    onPreviewRef.current?.(url, index);
  }, []);
  const favoriteStackImage = useCallback((url: string) => {
    onFavoriteRef.current?.(url);
  }, []);
  const publishStackImage = useCallback((url: string) => {
    onPublishRef.current?.(url);
  }, []);
  const regenerateStackImage = useCallback((index: number) => {
    onRegenerateSingleRef.current?.(index);
  }, []);
  // 回调是否可用压缩成布尔值下传，列表项内部据此决定是否渲染对应入口。
  const favoriteSupported = Boolean(onFavorite);
  const publishSupported = Boolean(onPublish);
  const showFailedRegenerate = Boolean(onRegenerateSingle);
  const insertSupported = Boolean(onInsertReferenceFiles);
  // 配置格/缩略图点击立即同步父级；滚动路径只更新本地索引并在 idle 后提交。
  const commitActiveIndex = useCallback((next: number) => {
    if (parentActiveIndexTimerRef.current) {
      clearTimeout(parentActiveIndexTimerRef.current);
      parentActiveIndexTimerRef.current = null;
    }
    pendingParentActiveIndexRef.current = null;
    activeIndexRef.current = next;
    setLocalActiveIndex(next);
    onActiveIndexChangeRef.current?.(next);
  }, []);
  const flushParentActiveIndexCommit = useCallback(() => {
    if (parentActiveIndexTimerRef.current) {
      clearTimeout(parentActiveIndexTimerRef.current);
      parentActiveIndexTimerRef.current = null;
    }
    const next = pendingParentActiveIndexRef.current;
    pendingParentActiveIndexRef.current = null;
    if (next != null) onActiveIndexChangeRef.current?.(next);
  }, []);
  const scheduleParentActiveIndexCommit = useCallback((next: number) => {
    pendingParentActiveIndexRef.current = next;
    if (parentActiveIndexTimerRef.current) clearTimeout(parentActiveIndexTimerRef.current);
    parentActiveIndexTimerRef.current = setTimeout(flushParentActiveIndexCommit, PARENT_COMMIT_IDLE_FALLBACK_MS);
  }, [flushParentActiveIndexCommit]);
  // 滚动事件每次触发时调用：只要存在待提交索引就清掉并重置兜底定时器，
  // 保证滚动持续期间永不向父级提交；滚动真正停止（scrollend 立即 flush，
  // 或事件静默超过兜底间隔）才同步左侧配置卡高亮。
  const noteScrollActivity = useCallback(() => {
    if (pendingParentActiveIndexRef.current == null) return;
    if (parentActiveIndexTimerRef.current) clearTimeout(parentActiveIndexTimerRef.current);
    parentActiveIndexTimerRef.current = setTimeout(flushParentActiveIndexCommit, PARENT_COMMIT_IDLE_FALLBACK_MS);
  }, [flushParentActiveIndexCommit]);

  useEffect(() => {
    if (controlledActiveIndex == null) return;
    setLocalActiveIndex(Math.max(0, Math.min(
      controlledActiveIndex,
      Math.max(0, selectionCount - 1),
    )));
  }, [controlledActiveIndex, selectionCount]);

  const refreshItemCenters = useCallback(() => {
    const referenceScroll = referenceScrollRef.current;
    const outputScroll = outputScrollRef.current;
    const referenceScrollRect = referenceScroll?.getBoundingClientRect();
    const outputScrollRect = outputScroll?.getBoundingClientRect();
    referenceItemCentersRef.current = referenceImageRefs.current
      .slice(0, referenceCountRef.current)
      .map((element) => {
        if (!element || !referenceScroll || !referenceScrollRect) return 0;
        const rect = element.getBoundingClientRect();
        return referenceScroll.scrollTop + rect.top - referenceScrollRect.top + rect.height / 2;
      });
    outputItemCentersRef.current = outputImageRefs.current
      .slice(0, outputSlotCountRef.current)
      .map((element) => {
        if (!element || !outputScroll || !outputScrollRect) return 0;
        const rect = element.getBoundingClientRect();
        return outputScroll.scrollTop + rect.top - outputScrollRect.top + rect.height / 2;
      });
  }, []);
  // 顶部「画布排版」徽章与「下载完整详情页」按钮锚定在效果图列几何上，
  // 除挂载/依赖变化外还须随图片加载、列宽变化等几何事件重算，避免残留陈旧偏移。
  const syncCanvasBadgeLeft = useCallback(() => {
    const toolbar = toolbarRef.current;
    const root = resultRootRef.current;
    const labels = root?.querySelectorAll<HTMLElement>("[data-detail-replica-result-label]");
    const effectLabel = labels?.[1];
    if (!toolbar || !effectLabel) return;
    const nextLeft = effectLabel.getBoundingClientRect().left - toolbar.getBoundingClientRect().left;
    setCanvasBadgeLeft((prev) => (prev != null && Math.abs(prev - nextLeft) < 0.5 ? prev : nextLeft));
  }, []);

  const syncDownloadButtonLeft = useCallback(() => {
    const toolbar = toolbarRef.current;
    const root = resultRootRef.current;
    const imageToolbar = root?.querySelector<HTMLElement>(
      "[data-detail-replica-result-image-toolbar]",
    );
    const outputImage = root?.querySelector<HTMLElement>(
      "[data-detail-replica-result-output-image]",
    );
    if (!toolbar || (!imageToolbar && !outputImage)) return;

    const toolbarRect = toolbar.getBoundingClientRect();
    let nextLeft: number;
    if (imageToolbar) {
      nextLeft = imageToolbar.getBoundingClientRect().left - toolbarRect.left;
    } else {
      const imageRect = outputImage!.getBoundingClientRect();
      nextLeft = imageRect.right - toolbarRect.left + 10;
    }
    setDownloadButtonLeft((prev) => (
      prev != null && Math.abs(prev - nextLeft) < 0.5 ? prev : nextLeft
    ));
  }, []);

  const scheduleGeometryRefresh = useCallback(() => {
    if (geometryRefreshRafRef.current !== null) return;
    geometryRefreshRafRef.current = requestAnimationFrame(() => {
      geometryRefreshRafRef.current = null;
      refreshItemCenters();
      syncCanvasBadgeLeft();
      syncDownloadButtonLeft();
    });
  }, [refreshItemCenters, syncCanvasBadgeLeft, syncDownloadButtonLeft]);

  const recordImageAspect = useCallback((
    column: "reference" | "output",
    url: string,
    image: HTMLImageElement,
  ) => {
    // 已卸载/替换的旧图迟到时，不能把尺寸写回当前历史或替换图。
    if (!currentPreviewUrlsRef.current[column].includes(url)) return;
    const { naturalWidth, naturalHeight } = image;
    if (naturalWidth > 0 && naturalHeight > 0) {
      const aspect = naturalWidth / naturalHeight;
      const cache = column === "reference" ? measuredReferenceAspectsRef.current : measuredOutputAspectsRef.current;
      if (cache.get(url) !== aspect) {
        cache.set(url, aspect);
        setMeasuredAspectVersion((version) => version + 1);
      }
    }
    scheduleGeometryRefresh();
  }, [scheduleGeometryRefresh]);
  const handleReferenceImageLoad = useCallback((url: string, image: HTMLImageElement) => {
    recordImageAspect("reference", url, image);
  }, [recordImageAspect]);
  const handleOutputImageLoad = useCallback((url: string, image: HTMLImageElement) => {
    recordImageAspect("output", url, image);
  }, [recordImageAspect]);
  const insertUploadingRef = useRef(insertUploading);
  insertUploadingRef.current = insertUploading;

  const {
    images: imagesRef,
    onImagesUpdated: onImagesUpdatedRef,
    taskId: taskIdRef,
    batchChildTaskIds: batchChildTaskIdsRef,
    onHistoryRefresh: onHistoryRefreshRef,
  } = useLatestRefs({ images, onImagesUpdated, taskId, batchChildTaskIds, onHistoryRefresh });
  const { refiningIndexes, handleRefineStart } = useRefineLifecycle({
    ownerTabId: refineOwnerTabId,
    imageIndex: refineImageIndex,
    taskId,
    batchChildTaskIds,
    activeRefines,
    sourceImages: images,
    lifecycleVersion: refineStateVersion,
    onTrack: onRefineTrack,
    onHistoryRefresh,
    onFailure: (message) => setToastMessage({ text: message, type: "error" }),
  });

  useEffect(() => {
    return () => {
      if (linkedScrollRafRef.current !== null) {
        cancelAnimationFrame(linkedScrollRafRef.current);
        linkedScrollRafRef.current = null;
      }
      if (geometryRefreshRafRef.current !== null) {
        cancelAnimationFrame(geometryRefreshRafRef.current);
        geometryRefreshRafRef.current = null;
      }
      const peerFollowState = peerFollowRef.current;
      if (peerFollowState && peerFollowState.raf !== null) {
        cancelAnimationFrame(peerFollowState.raf);
      }
      peerFollowRef.current = null;
      if (parentActiveIndexTimerRef.current) {
        clearTimeout(parentActiveIndexTimerRef.current);
      }
    };
  }, []);

  useLayoutEffect(() => {
    scheduleGeometryRefresh();
    const elements = [
      ...referenceImageRefs.current.slice(0, referenceCount),
      ...outputImageRefs.current.slice(0, outputSlotCount),
    ].filter((element): element is HTMLElement => Boolean(element));
    const observer = new ResizeObserver(scheduleGeometryRefresh);
    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [insertSlotIndex, outputSlotCount, ratios, referenceCount, scheduleGeometryRefresh]);

  useEffect(() => {
    const referenceScroll = referenceScrollRef.current;
    const outputScroll = outputScrollRef.current;
    const handleScrollEnd = () => {
      flushParentActiveIndexCommit();
      // 滚动真正结束：释放用户驱动列的所有权，让下一次键盘滚动可切换驱动列；
      // 软跟随写入触发的 scrollend 走同一分支，同样安全。
      userDrivenColumnRef.current = null;
    };
    referenceScroll?.addEventListener("scrollend", handleScrollEnd);
    outputScroll?.addEventListener("scrollend", handleScrollEnd);
    return () => {
      referenceScroll?.removeEventListener("scrollend", handleScrollEnd);
      outputScroll?.removeEventListener("scrollend", handleScrollEnd);
    };
  }, [flushParentActiveIndexCommit]);

  useEffect(() => {
    if (!toastMessage) return;
    const timer = setTimeout(() => setToastMessage(null), 3000);
    return () => clearTimeout(timer);
  }, [toastMessage]);

  useLayoutEffect(() => {
    const rail = referenceThumbRailRef.current;
    if (!rail) return;
    const update = () => {
      setReferenceThumbSize(resolveDetailReplicaThumbnailSize(rail.clientHeight, referenceCountRef.current));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(rail);
    return () => observer.disconnect();
  }, [referenceCount]);

  useLayoutEffect(() => {
    const rail = outputThumbRailRef.current;
    if (!rail) return;
    const update = () => {
      setOutputThumbSize(resolveDetailReplicaThumbnailSize(rail.clientHeight, outputSlotCountRef.current));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(rail);
    return () => observer.disconnect();
  }, [outputSlotCount]);

  useLayoutEffect(() => {
    syncCanvasBadgeLeft();
    syncDownloadButtonLeft();
    const root = resultRootRef.current;
    if (!root) return;
    const syncToolbarPositions = () => {
      syncCanvasBadgeLeft();
      syncDownloadButtonLeft();
    };
    const observer = new ResizeObserver(syncToolbarPositions);
    observer.observe(root);
    window.addEventListener("resize", syncToolbarPositions);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", syncToolbarPositions);
    };
  }, [
    syncCanvasBadgeLeft,
    syncDownloadButtonLeft,
    outputSlotCount,
    referenceThumbSize.scale,
    outputThumbSize.scale,
    status,
    validImages.length,
  ]);

  useEffect(() => {
    if (localActiveIndex > selectionCount - 1) {
      commitActiveIndex(Math.max(0, selectionCount - 1));
    }
  }, [commitActiveIndex, localActiveIndex, selectionCount]);

  // 程序化写入对侧 scrollTop 并登记记录：随后的自写回声 scroll 事件据此被识别并吞掉。
  const writePeerScrollTop = useCallback((
    column: "reference" | "output",
    el: HTMLDivElement,
    top: number,
  ) => {
    el.scrollTop = top;
    lastPeerWriteRef.current = { column, top: el.scrollTop };
  }, []);

  // 终止进行中的软跟随动画（缩略图/配置格定位、用户抓取对侧、组件卸载时调用）。
  const cancelPeerFollow = useCallback(() => {
    const state = peerFollowRef.current;
    if (!state) return;
    if (state.raf !== null) cancelAnimationFrame(state.raf);
    peerFollowRef.current = null;
  }, []);

  // 软跟随帧：对侧每帧向目标位置收敛剩余距离的固定比例，进入吸附阈值即对齐并结束。
  const stepPeerFollow = useCallback(function stepPeerFollowFrame() {
    const state = peerFollowRef.current;
    if (!state) return;
    const el = state.follower === "reference" ? referenceScrollRef.current : outputScrollRef.current;
    if (!el) {
      cancelPeerFollow();
      return;
    }
    const current = el.scrollTop;
    // 首次测量会校正列表高度，旧目标可能已经超出浏览器可滚动范围。
    state.targetTop = Math.max(0, Math.min(state.targetTop, el.scrollHeight - el.clientHeight));
    const delta = state.targetTop - current;
    if (Math.abs(delta) <= PEER_FOLLOW_SETTLE_PX) {
      if (delta !== 0) writePeerScrollTop(state.follower, el, state.targetTop);
      cancelPeerFollow();
      return;
    }
    writePeerScrollTop(state.follower, el, current + delta * PEER_FOLLOW_EASE);
    if (el.scrollTop === current) {
      cancelPeerFollow();
      return;
    }
    state.raf = requestAnimationFrame(stepPeerFollowFrame);
  }, [cancelPeerFollow, writePeerScrollTop]);

  const scrollStackToIndex = useCallback((
    scrollEl: HTMLDivElement | null,
    imageEls: Array<HTMLElement | null>,
    index: number,
    itemCount: number,
  ) => {
    if (!scrollEl) return;
    // 定位前终止进行中的软跟随动画，避免跟随写入与定位滚动互相拉扯。
    cancelPeerFollow();
    const target = imageEls[index];
    if (!target) return;
    // 立即终止仍在进行中的上一次平滑滚动：否则新定位判定「目标已完整可见」时，
    // 旧动画会继续滚到旧目标，把新选中的参考图/效果图裁在可视区外。
    scrollEl.scrollTop = scrollEl.scrollTop;
    // 滚动容器未设 position，offsetTop 相对的是更外层的 offsetParent（含头部/工具栏等
    // 恒定偏移），直接拿它当 content 坐标会让定位整体偏大，把选中图的上边缘裁出可视区。
    // 用 getBoundingClientRect 换算成 content 坐标，与左侧工作区 reveal 逻辑保持一致。
    const scrollRect = scrollEl.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const nextTop = revealReferenceScrollTop({
      scrollTop: scrollEl.scrollTop,
      viewportHeight: scrollEl.clientHeight,
      contentHeight: scrollEl.scrollHeight,
      targetTop: scrollEl.scrollTop + targetRect.top - scrollRect.top,
      targetHeight: targetRect.height,
    });
    if (Math.abs(scrollEl.scrollTop - nextTop) < 1) return;
    const behavior = resolveDetailReplicaRevealBehavior({
      itemCount,
      scrollDistance: nextTop - scrollEl.scrollTop,
      viewportHeight: scrollEl.clientHeight,
    });
    programmaticScrollUntilRef.current = Date.now() + resolveDetailReplicaScrollGuardMs(behavior);
    scrollEl.scrollTo({
      top: nextTop,
      behavior,
    });
  }, [cancelPeerFollow]);

  useEffect(() => {
    if (!revealTarget) return;
    scrollStackToIndex(
      referenceScrollRef.current,
      referenceImageRefs.current,
      revealTarget.index,
      referenceCount,
    );
    scrollStackToIndex(
      outputScrollRef.current,
      outputImageRefs.current,
      revealTarget.index,
      outputSlotCount,
    );
  }, [outputSlotCount, referenceCount, revealTarget, scrollStackToIndex]);

  // 末张展开插入框后，新增的 132px 上传框会把可滚动内容向下撑开；
  // 结果区原先只把参考图滚进视口，上传框会落在底部裁切区外，需同步露出该框。
  useEffect(() => {
    if (insertSlotIndex == null) return;
    const root = referenceScrollRef.current;
    const placeholder = insertPlaceholderRef.current;
    if (!root || !placeholder) return;
    const rootRect = root.getBoundingClientRect();
    const placeholderRect = placeholder.getBoundingClientRect();
    const nextScrollTop = Math.min(
      root.scrollTop + Math.max(0, placeholderRect.bottom - rootRect.bottom),
      Math.max(0, root.scrollHeight - root.clientHeight),
    );
    if (Math.abs(nextScrollTop - root.scrollTop) < 1) return;
    programmaticScrollUntilRef.current = Date.now() + resolveDetailReplicaScrollGuardMs("auto");
    root.scrollTo({ top: nextScrollTop });
  }, [insertSlotIndex]);

  const selectIndex = useCallback((index: number) => {
    const next = Math.max(0, Math.min(index, Math.max(0, selectionCount - 1)));
    explicitSelectionRef.current = true;
    userDrivenColumnRef.current = null;
    commitActiveIndex(next);
    revealTokenRef.current += 1;
    setRevealTarget({ index: next, token: revealTokenRef.current });
  }, [commitActiveIndex, selectionCount]);

  // 参考图「插入图片」上传：委托 page.tsx 的 onInsertReferenceFiles 执行真实上传与槽位插，
  // 此处只负责上传态与展开态收起，与工作区 uploadFiles 的 UI 行为对齐。
  // 上传中的重复提交守卫改读 insertUploadingRef.current，让本回调依赖为空、
  // 引用在整个会话内稳定，作为 props 传入 memo 化的参考图项不会破坏比较。
  const handleInsertUpload = useCallback(async (files: FileList, insertAfterIndex: number) => {
    const handler = onInsertReferenceFilesRef.current;
    if (!handler || insertUploadingRef.current) return;
    setInsertUploading(true);
    try {
      await handler(files, insertAfterIndex);
      setInsertSlotIndex((currentIndex) => (
        currentIndex === insertAfterIndex ? null : currentIndex
      ));
    } finally {
      setInsertUploading(false);
    }
  }, []);

  // 插入框展开/收起与拖拽态写入的稳定回调：setInsertSlotIndex / setInsertDragOverIndex
  // 的函数式更新不依赖外部状态，包成 useCallback([]) 后传给 memo 项保持引用稳定。
  const toggleInsertSlot = useCallback((index: number) => {
    setInsertSlotIndex((currentIndex) => (currentIndex === index ? null : index));
  }, []);
  const changeInsertDragState = useCallback((index: number, active: boolean) => {
    setInsertDragOverIndex((currentIndex) => (
      active ? index : (currentIndex === index ? null : currentIndex)
    ));
  }, []);

  // 选中参考图后按 Delete/Backspace 删除当前屏，Ctrl/Meta+Z 撤回最近一次删除；
  // 与工作区 DetailPageReplicaWorkspace 的快捷键行为保持一致。
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (document.querySelector('[data-image-edit-modal="true"]')) return;
      if (event.defaultPrevented || isKeyboardEditableTarget(event.target)) return;
      const isDeleteKey = event.key === "Delete"
        || event.key === "Backspace"
        || event.code === "Delete"
        || event.code === "Backspace";
      if (isDeleteKey && !event.ctrlKey && !event.metaKey && !event.altKey) {
        event.preventDefault();
        if (!event.repeat) onDeleteReferenceRef.current?.(activeIndexRef.current);
        return;
      }
      if (
        (event.ctrlKey || event.metaKey)
        && !event.altKey
        && !event.shiftKey
        && event.key.toLowerCase() === "z"
      ) {
        event.preventDefault();
        onUndoReferenceDeletionRef.current?.();
      }
    };
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, []);

  // 外部配置格切换（恢复参数后点击配置）时，联动参考图与效果图滚动到对应屏
  useEffect(() => {
    if (!focusRequest) return;
    selectIndex(focusRequest.index);
  }, [focusRequest, selectIndex]);

  const syncActiveFromScroll = (scrollEl: HTMLDivElement | null, itemCenters: readonly number[]) => {
    if (!scrollEl) return;
    const midpoint = scrollEl.scrollTop + scrollEl.clientHeight / 2;
    const bestIndex = findClosestDetailReplicaItemIndex(itemCenters, midpoint);
    if (activeIndexRef.current === bestIndex) return;
    activeIndexRef.current = bestIndex;
    setLocalActiveIndex(bestIndex);
    scheduleParentActiveIndexCommit(bestIndex);
  };

  const processLinkedStackScroll = (source: "reference" | "output") => {
    if (explicitSelectionRef.current) return;
    const sourceEl = source === "reference" ? referenceScrollRef.current : outputScrollRef.current;
    const targetEl = source === "reference" ? outputScrollRef.current : referenceScrollRef.current;
    const sourceItemCenters = source === "reference"
      ? referenceItemCentersRef.current
      : outputItemCentersRef.current;
    if (!sourceEl) return;

    // 程序化滚动（缩略图点击定位）期间保持点击时的选中态，避免途经屏反复重渲染。
    if (Date.now() < programmaticScrollUntilRef.current) {
      return;
    }

    // 所有权：只有用户正在驱动的列参与联动计算，被我们软跟随写入的对侧滚动事件一律
    // 忽略，从根源上消除「对侧再反算回来」的双向纠正抖动。
    if (userDrivenColumnRef.current && source !== userDrivenColumnRef.current) return;

    // 自写回声：最后一次程序化写入引发的 scroll 事件一次性吞掉；若列匹配但位置已偏移
    // ≥2px，说明是用户键盘等外部驱动接管，清除记录后照常联动。
    const lastWrite = lastPeerWriteRef.current;
    if (lastWrite && lastWrite.column === source) {
      lastPeerWriteRef.current = null;
      if (Math.abs(sourceEl.scrollTop - lastWrite.top) < 2) return;
    }

    // 键盘滚动没有 wheel/pointer 前奏：首个滚动事件即认领该列所有权。
    if (userDrivenColumnRef.current == null) userDrivenColumnRef.current = source;

    syncActiveFromScroll(sourceEl, sourceItemCenters);

    if (!targetEl) return;
    const nextTop = syncPeerScrollTop({
      sourceScrollTop: sourceEl.scrollTop,
      sourceScrollHeight: sourceEl.scrollHeight,
      sourceClientHeight: sourceEl.clientHeight,
      targetScrollHeight: targetEl.scrollHeight,
      targetClientHeight: targetEl.clientHeight,
    });
    if (Math.abs(targetEl.scrollTop - nextTop) < PEER_FOLLOW_SETTLE_PX) return;

    // 启动/刷新软跟随：对侧不再逐帧硬写目标位置，而是渐进追赶同一位置，
    // 目标变化时只更新 targetTop，让进行中的动画平滑转向新目标。
    const follower = source === "reference" ? "output" : "reference";
    if (peerFollowRef.current && peerFollowRef.current.follower !== follower) {
      cancelPeerFollow();
    }
    if (peerFollowRef.current) {
      peerFollowRef.current.targetTop = nextTop;
    } else {
      peerFollowRef.current = { follower, targetTop: nextTop, raf: null };
    }
    if (peerFollowRef.current.raf === null) {
      peerFollowRef.current.raf = requestAnimationFrame(stepPeerFollow);
    }
  };

  const scheduleLinkedStackScroll = (source: "reference" | "output") => {
    // 两列任一滚动事件都视为「滚动进行中」，重置父级索引提交的兜底定时器。
    noteScrollActivity();
    if (explicitSelectionRef.current) return;
    if (Date.now() < programmaticScrollUntilRef.current) return;
    // 自己软跟随写入引发的回声事件不进 pending，保证滚动驱动源唯一。
    if (peerFollowRef.current?.follower === source) return;
    pendingLinkedScrollSourceRef.current = source;
    if (linkedScrollRafRef.current !== null) return;
    linkedScrollRafRef.current = requestAnimationFrame(() => {
      linkedScrollRafRef.current = null;
      const pendingSource = pendingLinkedScrollSourceRef.current;
      pendingLinkedScrollSourceRef.current = null;
      if (pendingSource) processLinkedStackScroll(pendingSource);
    });
  };

  // 用户接管某列（wheel/pointer 前奏）：认领该列所有权并清掉程序化滚动 guard；
  // 若直接抓住正在被软跟随驱动的列，立即取消跟随动画，改由用户驱动。
  const handleUserScrollStart = useCallback((column: "reference" | "output") => {
    explicitSelectionRef.current = false;
    userDrivenColumnRef.current = column;
    programmaticScrollUntilRef.current = 0;
    if (peerFollowRef.current?.follower === column) cancelPeerFollow();
  }, [cancelPeerFollow]);

  const slotState = (index: number) => {
    const image = images[index] || "";
    const failed = failedIndexes.includes(index) || (status === "failed" && !image);
    // 仅「生成中」态显示生图卡片；「已完成」态下插入的新屏尚无效果图，
    // 应作为「等待结果」占位，由后续生成填入，避免误显生图界面。
    const generating = regeneratingIndexes.includes(index)
      || (!image && !failed && status === "generating");
    return { image, failed, generating };
  };

  const handleOpenRefine = useCallback((imgUrl: string, imgIndex: number) => {
    setRefineImageUrl(imgUrl);
    setRefineImageIndex(imgIndex);
    setRefineEditorOpen(true);
  }, []);

  const imageEditItems = useMemo(
    () => images
      .map((url, idx) => ({ id: `detail-replica-image-${idx}`, url, index: idx }))
      .filter((item) => !!item.url),
    [images],
  );

  const handleOpenImageEdit = useCallback((idx: number) => {
    const item = imageEditItems.find((candidate) => candidate.index === idx) || imageEditItems[0];
    if (!item) {
      setToastMessage({ text: "E8101 编辑参数有误，请调整后重试", type: "error" });
      return;
    }
    setImageEditInitialId(item.id);
    setImageEditOpen(true);
  }, [imageEditItems]);

  const handleImageEditSave = useCallback(async (results: ImageEditSaveResult[]) => {
    if (results.length === 0) return;
    const resolved = replaceEditedImages(imagesRef.current, results);
    if (resolved.replacements.length === 0) {
      throw new Error("E8102 图片保存失败，请稍后重试");
    }

    const patchJobs = resolved.replacements.map((replacement) => {
      const targetTaskId = batchChildTaskIdsRef.current?.[replacement.targetIndex] || taskIdRef.current;
      if (!targetTaskId) throw new Error("E8102 图片保存失败，请稍后重试");
      const patchIndex = batchChildTaskIdsRef.current?.[replacement.targetIndex]
        ? 0
        : replacement.targetIndex;
      return patchTaskOutputImage(targetTaskId, patchIndex, replacement.editedUrl);
    });

    await Promise.all(patchJobs);
    onImagesUpdatedRef.current?.(resolved.images, taskIdRef.current);
    onHistoryRefreshRef.current?.();
    setToastMessage({ text: "调色结果已保存", type: "success" });
  }, []);

  return (
    <div
      ref={resultRootRef}
      data-detail-replica-result
      className="h-full min-h-0 overflow-x-auto bg-[#000] text-white"
    >
      <div
        data-detail-replica-result-scroll-content
        className="flex h-full min-h-0 min-w-full flex-1 flex-col"
        style={{ minWidth: DETAIL_REPLICA_RESULT_MIN_STACK_WIDTH_PX }}
      >
      {refineEditorOpen && (
        <ImageRefineEditor
          imageUrl={refineImageUrl}
          taskId={taskId}
          imageIndex={refineImageIndex}
          productImage={productImage || undefined}
          onClose={() => setRefineEditorOpen(false)}
          onRefineStart={handleRefineStart}
        />
      )}
      {imageEditOpen && (
        <ImageEditModal
          open={imageEditOpen}
          mode="single"
          images={imageEditItems}
          initialImageId={imageEditInitialId}
          taskId={taskId}
          previewLayout="stack"
          onClose={() => setImageEditOpen(false)}
          onSave={handleImageEditSave}
        />
      )}
      {toastMessage && (
        <div
          className={`fixed top-6 left-1/2 z-200 -translate-x-1/2 rounded-lg px-5 py-2.5 text-sm font-medium shadow-lg backdrop-blur-xs ${
            toastMessage.type === "success" ? "bg-green-500/90 text-white" : "bg-red-500/90 text-white"
          }`}
        >
          {toastMessage.text}
        </div>
      )}

      <div
        ref={toolbarRef}
        data-detail-replica-result-toolbar
        className="relative flex h-14 shrink-0 items-center px-5"
      >
        <span
          data-detail-replica-result-canvas-badge
          className="absolute top-1/2 flex h-8 -translate-y-1/2 items-center rounded-[8px] border border-[#272727] px-3 text-[12px] text-white/80"
          style={{
            left: canvasBadgeLeft ?? 0,
            opacity: canvasBadgeLeft == null ? 0 : 1,
          }}
        >
          画布排版 · {validImages.length}/{outputSlotCount}张
        </span>
        {/* ecom-scope.css 将 .ecom-button 的 transform 重置为 none，按钮本体不能用
            -translate-y-1/2 居中；定位交给外层 span，与「画布排版」徽章共用同一套
            垂直居中机制保持齐平，左缘对齐下方图片工具栏（下载图标列）。 */}
        <span
          className="absolute top-1/2 z-10 flex -translate-y-1/2"
          style={{
            left: downloadButtonLeft ?? 0,
            visibility: downloadButtonLeft == null ? "hidden" : "visible",
          }}
        >
          <Button
            type="button"
            variant="outline"
            size="mini"
            data-detail-replica-result-download-all
            disabled={validImages.length === 0}
            onClick={() => void downloadFiles(validImages, "flowx_detail_page")}
          >
            <Download data-icon="inline-start" size={13} />
            下载完整详情页
          </Button>
        </span>
      </div>

      <div
        data-detail-replica-result-stack
        className="flex min-h-0 flex-1 overflow-hidden px-5 pb-5"
      >
        <div
          data-detail-replica-result-stack-content
          className="flex min-h-0 w-full items-stretch justify-center"
          style={{
            minWidth: DETAIL_REPLICA_RESULT_MIN_STACK_WIDTH_PX - 40,
            gap: `clamp(${DETAIL_REPLICA_RESULT_MIN_COLUMN_GAP_PX}px, 5vw, 20px)`,
          }}
        >
        <ResultColumnGroup
          label="参考图"
          // 参考图列滚动容器有 pl-[116px] 给「插入图片」按钮留的左贴边区，
          // 标签需平移过该区才能像效果图标签一样紧贴 400px 图片；窄屏贴边区收起为 0。
          labelShiftClass="translate-x-[116px] max-[700px]:translate-x-0"
        >
          <ResultStackColumn
            scrollRef={referenceScrollRef}
            stackAttr="data-detail-replica-result-reference-stack"
            stackWidthClass="w-[400px]"
            allowLeftOverflow
            onScroll={() => scheduleLinkedStackScroll("reference")}
            onUserScrollStart={() => handleUserScrollStart("reference")}
          >
            {Array.from({ length: referenceCount }, (_, index) => {
              const image = resolvedReferences[index] || "";
              const previewImage = referencePreviewImages[index] || image;
              const selected = safeActive === index;
              const shouldMountImage = index >= windowRange.start && index <= windowRange.end;
              return (
                <ReferenceStackItem
                  key={`detail-replica-result-ref-${index}`}
                  index={index}
                  image={image}
                  previewImage={previewImage}
                  selected={selected}
                  inWindow={shouldMountImage}
                  aspectRatio={measuredReferenceAspectsRef.current.get(previewImage) ?? itemAspectRatios[index]}
                  showInsertTrigger={selected && insertSupported}
                  insertOpen={insertSlotIndex === index}
                  insertUploading={insertUploading}
                  insertDragOver={insertDragOverIndex === index}
                  itemRefs={referenceImageRefs}
                  insertPlaceholderRef={insertPlaceholderRef}
                  onSelect={selectIndex}
                  onToggleInsertSlot={toggleInsertSlot}
                  handleInsertUpload={handleInsertUpload}
                  onInsertDragStateChange={changeInsertDragState}
                  onImageLoad={handleReferenceImageLoad}
                />
              );
            })}
          </ResultStackColumn>

          <FilmStripThumbnailRail
            railRef={referenceThumbRailRef}
            railAttr="data-detail-replica-result-reference-thumb-rail"
            count={referenceCount}
            activeIndex={safeActive}
            urls={referenceThumbnailImages}
            isFailedSlot={() => false}
            width={referenceThumbSize.width}
            height={referenceThumbSize.height}
            scale={referenceThumbSize.scale}
            onSelect={selectIndex}
          />
        </ResultColumnGroup>

        <ResultColumnGroup label="效果图">
          <ResultStackColumn
            scrollRef={outputScrollRef}
            stackAttr="data-detail-replica-result-output-stack"
            stackWidthClass="w-auto"
            onScroll={() => scheduleLinkedStackScroll("output")}
            onUserScrollStart={() => handleUserScrollStart("output")}
          >
            {Array.from({ length: outputSlotCount }, (_, index) => {
              const { image, failed, generating } = slotState(index);
              const previewImage = outputPreviewImages[index] || image;
              const selected = safeActive === index;
              const shouldMountImage = index >= windowRange.start && index <= windowRange.end;
              const refineOverlayVisible = Boolean(activeRefines && index in activeRefines)
                || index in refiningIndexes;
              return (
                <OutputStackItem
                  key={`detail-replica-result-out-${index}`}
                  index={index}
                  image={image}
                  previewImage={previewImage}
                  selected={selected}
                  inWindow={shouldMountImage}
                  aspectRatio={measuredOutputAspectsRef.current.get(previewImage) ?? itemAspectRatios[index]}
                  failed={failed}
                  generating={generating}
                  progress={taskProgresses[index] ?? 0}
                  startedAt={generationStartedAt}
                  refineOverlayVisible={refineOverlayVisible}
                  refineStartedAt={activeRefines?.[index]?.startedAt}
                  showFailedRegenerate={showFailedRegenerate}
                  favoriteSupported={favoriteSupported}
                  publishSupported={publishSupported}
                  allImages={validImages}
                  itemRefs={outputImageRefs}
                  onSelect={selectIndex}
                  onPreview={previewStackImage}
                  onFavorite={favoriteStackImage}
                  onPublish={publishStackImage}
                  onRegenerateSingle={regenerateStackImage}
                  handleOpenRefine={handleOpenRefine}
                  handleOpenImageEdit={handleOpenImageEdit}
                  onImageLoad={handleOutputImageLoad}
                />
              );
            })}
          </ResultStackColumn>

          <FilmStripThumbnailRail
            railRef={outputThumbRailRef}
            railAttr="data-detail-replica-result-output-thumb-rail"
            count={outputSlotCount}
            activeIndex={safeActive}
            urls={outputThumbnailImages}
            isFailedSlot={(index) => {
              const image = images[index] || "";
              return failedIndexes.includes(index) || (status === "failed" && !image);
            }}
            width={outputThumbSize.width}
            height={outputThumbSize.height}
            scale={outputThumbSize.scale}
            onSelect={selectIndex}
          />
        </ResultColumnGroup>
        </div>
      </div>
      </div>
    </div>
  );
}

function ResultColumnGroup({
  label,
  labelShiftClass,
  children,
}: {
  label: string;
  /** 参考图列传 translate-x-[116px]：平移插入图片按钮预留的左侧贴边区，使标签紧贴图片（与效果图列一致） */
  labelShiftClass?: string;
  children: ReactNode;
}) {
  return (
    <div
      data-detail-replica-result-column
      className="flex h-full min-h-0 shrink-0 items-start"
      style={{ gap: DETAIL_REPLICA_RESULT_LABEL_IMAGE_GAP_PX }}
    >
      {/* 固定在图片视口左上侧，不随长图滚动；背景对齐 Figma 1610:20084 pill */}
      <span
        data-detail-replica-result-label
        className={`inline-flex h-5 shrink-0 self-start items-center justify-center bg-[url('/button_bg9.png')] bg-[length:100%_100%] bg-center bg-no-repeat text-center text-[12px] leading-none text-[#E6E6E6] ${labelShiftClass ?? ""}`}
        style={{ width: DETAIL_REPLICA_RESULT_LABEL_WIDTH_PX }}
      >
        {label}
      </span>
      <div className="flex h-full min-h-0 min-w-0 items-stretch gap-5">
        {children}
      </div>
    </div>
  );
}

function ResultStackColumn({
  children,
  scrollRef,
  stackAttr,
  stackWidthClass,
  allowLeftOverflow = false,
  onScroll,
  onUserScrollStart,
}: {
  children: ReactNode;
  scrollRef: MutableRefObject<HTMLDivElement | null>;
  stackAttr: "data-detail-replica-result-reference-stack" | "data-detail-replica-result-output-stack";
  stackWidthClass: string;
  // 参考图列选中态会在图片左侧渲染「插入图片」按钮（right-[calc(100%+12px)]），
  // 该按钮向左溢出滚动容器。若沿用 overflow-x-hidden 会被裁掉，故参考图列改为
  // 仅纵向滚动 + 左侧预留 116px 贴边空间（与工作区 pl-[116px] 同思路），让按钮可见。
  allowLeftOverflow?: boolean;
  onScroll: () => void;
  onUserScrollStart: () => void;
}) {
  const stackProps = { [stackAttr]: true } as Record<string, boolean>;
  // 参考图列额外预留 116px 左贴边空间给「插入图片」按钮，外层容器同步加宽，
  // 保证图片本身仍占 400px、按钮在 padding 区内可见且不被裁切。
  const outerWidthClass = allowLeftOverflow
    ? "w-[516px]"
    : stackWidthClass === "w-[400px]"
      ? "w-[400px]"
      : "";
  return (
    <div className={`flex h-full min-h-0 shrink-0 flex-col ${outerWidthClass}`}>
      <div
        ref={(node: HTMLDivElement | null) => { scrollRef.current = node; }}
        onScroll={onScroll}
        onWheel={onUserScrollStart}
        onPointerDown={onUserScrollStart}
        onKeyDown={(event) => {
          if (!event.defaultPrevented && !isKeyboardEditableTarget(event.target)
            && ["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End", " "].includes(event.key)) {
            onUserScrollStart();
          }
        }}
        data-detail-replica-result-scroll
        className={`min-h-0 flex-1 overflow-y-auto [scrollbar-color:rgba(255,255,255,.18)_transparent] [scrollbar-width:thin] ${
          allowLeftOverflow ? "pl-[116px] max-[700px]:pl-0" : "overflow-x-hidden"
        }`}
      >
        <div {...stackProps} className={`flex max-w-full flex-col ${stackWidthClass}`}>
          {children}
        </div>
      </div>
    </div>
  );
}

type ReferenceStackItemProps = {
  index: number;
  image: string;
  previewImage: string;
  selected: boolean;
  /** 是否处于活动屏前后共享窗口内（窗口外渲染等高占位而不挂载大图） */
  inWindow: boolean;
  aspectRatio: number;
  /** 当前选中屏且外部支持插入参考图时显示左下角「插入图片」入口 */
  showInsertTrigger: boolean;
  insertOpen: boolean;
  insertUploading: boolean;
  insertDragOver: boolean;
  /** 父级稳定的 ref 对象数组，组件内部按 index 回写，避免 per-index 闭包 ref */
  itemRefs: MutableRefObject<Array<HTMLElement | null>>;
  insertPlaceholderRef: Ref<HTMLLabelElement>;
  onSelect: (index: number) => void;
  onToggleInsertSlot: (index: number) => void;
  handleInsertUpload: (files: FileList, insertAfterIndex: number) => void | Promise<void>;
  onInsertDragStateChange: (index: number, active: boolean) => void;
  /** 图片首帧加载回调：父级借此记录该屏真实自然宽高比 */
  onImageLoad: (url: string, image: HTMLImageElement) => void;
};

// 参考图主列表项 memo 化：滚动越过屏边界时只有 setLocalActiveIndex 变化，
// 失选与选中两项之外的 props 完全一致，React 直接复用上次渲染结果，
// 不再重新协调全部 18 个含大图与「插入图片」按钮的子树。
// 所有 props 为基本类型或父级 useCallback/useMemo 稳定引用，保证 memo 比较生效。
const ReferenceStackItem = memo(function ReferenceStackItem({
  index,
  image,
  previewImage,
  selected,
  inWindow,
  aspectRatio,
  showInsertTrigger,
  insertOpen,
  insertUploading,
  insertDragOver,
  itemRefs,
  insertPlaceholderRef,
  onSelect,
  onToggleInsertSlot,
  handleInsertUpload,
  onInsertDragStateChange,
  onImageLoad,
}: ReferenceStackItemProps) {
  const imageRef = useCallback((element: HTMLImageElement | null) => {
    if (element?.complete && element.naturalWidth > 0) onImageLoad(previewImage, element);
  }, [onImageLoad, previewImage]);
  return (
    <Fragment>
    <div
      ref={(element) => { itemRefs.current[index] = element; }}
      data-detail-replica-result-reference-item={index + 1}
      className="relative"
      // contain:layout 隔离该项内部布局对滚动容器的溢出影响；
      // 不能加 paint ——「插入图片」按钮 right-[calc(100%+12px)] 需向左溢出绘制。
      style={{ contain: "layout" }}
    >
    <button
      type="button"
      data-detail-replica-result-image-frame
      data-detail-replica-result-selected-reference={selected ? index + 1 : undefined}
      onClick={() => onSelect(index)}
      className={`relative block w-full cursor-pointer border-[3px] border-solid text-left ${
        selected ? "border-[#4E7CFF]" : "border-transparent"
      }`}
      aria-current={selected ? "true" : undefined}
      aria-label={`查看第 ${index + 1} 屏参考图`}
      style={image ? undefined : { aspectRatio }}
    >
      {image && inWindow ? (
        <img
          key={previewImage}
          ref={imageRef}
          src={previewImage}
          alt={`第 ${index + 1} 屏参考图`}
          loading="lazy"
          decoding="async"
          onLoad={(event) => onImageLoad(previewImage, event.currentTarget)}
          style={{ aspectRatio: `auto ${aspectRatio}` }}
          className="block w-full h-auto"
        />
      ) : image ? (
        <span
          data-detail-replica-result-window-placeholder
          className="block w-full bg-[#111]"
          style={{ aspectRatio }}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-[#111] text-sm text-white/35">
          暂无参考图
        </div>
      )}
      <span className="absolute bottom-2 left-2 flex h-6 min-w-6 items-center justify-center rounded bg-black/70 px-1.5 text-[12px] text-white">
        {index + 1}
      </span>
    </button>
    {showInsertTrigger && (
      <button
        type="button"
        data-detail-replica-result-insert-trigger
        aria-expanded={insertOpen}
        disabled={insertUploading}
        onClick={() => onToggleInsertSlot(index)}
        className="absolute bottom-0 right-[calc(100%+12px)] z-10 flex h-8 cursor-pointer items-center gap-1 whitespace-nowrap rounded-[8px] border border-[#27282A] bg-black px-[15px] text-[13px] text-[#E6E6E6] hover:bg-[#111] disabled:cursor-not-allowed disabled:opacity-60"
      >
        插入图片
        <span className={`block size-3 transition-transform ${
          insertOpen ? "rotate-180" : ""
        }`}>
          <img
            src="/ecom/detail-replica-insert-chevron.svg"
            alt=""
            className="size-full"
          />
        </span>
      </button>
    )}
    </div>
    {insertOpen && (
      <label
        ref={insertPlaceholderRef}
        data-detail-replica-result-insert-placeholder
        data-detail-replica-insert-drop-active={insertDragOver ? "true" : undefined}
        aria-label={insertUploading ? "图片上传中" : "添加图片"}
        onDragOver={(event) => {
          event.preventDefault();
          if (!insertUploading) onInsertDragStateChange(index, true);
        }}
        onDragLeave={(event) => {
          if (event.currentTarget.contains(event.relatedTarget as Node)) return;
          onInsertDragStateChange(index, false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          onInsertDragStateChange(index, false);
          if (event.dataTransfer.files) void handleInsertUpload(event.dataTransfer.files, index);
        }}
        className={`mt-2 mb-2 flex h-[132px] w-full max-w-full cursor-pointer items-center justify-center rounded-[12px] border border-dashed bg-[#111] transition-colors ${
          insertDragOver
            ? "border-[#00FAE9] bg-[#102322] shadow-[0_0_0_1px_rgba(0,250,233,0.2)]"
            : "border-[#555] hover:border-[#888] hover:bg-[#151515]"
        } ${insertUploading ? "cursor-not-allowed opacity-70" : ""}`}
      >
        <input
          className="sr-only"
          type="file"
          accept={IMAGE_ACCEPT}
          multiple
          disabled={insertUploading}
          onChange={(event) => {
            if (event.target.files) void handleInsertUpload(event.target.files, index);
            event.currentTarget.value = "";
          }}
        />
        {insertUploading ? (
          <Loader2 className="size-5 animate-spin text-[#E6E6E6]" />
        ) : (
          <Plus className="size-5 text-[#E6E6E6]" />
        )}
      </label>
    )}
    </Fragment>
  );
});

type OutputStackItemProps = {
  index: number;
  image: string;
  previewImage: string;
  selected: boolean;
  /** 是否处于活动屏前后共享窗口内（窗口外渲染等高占位而不挂载大图） */
  inWindow: boolean;
  aspectRatio: number;
  failed: boolean;
  generating: boolean;
  progress: number;
  startedAt: number | null;
  refineOverlayVisible: boolean;
  refineStartedAt?: number;
  /** 失败态与工具栏的单屏重新生成入口是否可用（外部是否传入 onRegenerateSingle） */
  showFailedRegenerate: boolean;
  favoriteSupported: boolean;
  publishSupported: boolean;
  /** 面板级 useMemo 的有效效果图列表，images 不变时引用稳定 */
  allImages: string[];
  itemRefs: MutableRefObject<Array<HTMLElement | null>>;
  onSelect: (index: number) => void;
  onPreview?: (url: string, index: number) => void;
  onFavorite: (url: string) => void;
  onPublish: (url: string) => void;
  onRegenerateSingle: (index: number) => void;
  handleOpenRefine: (url: string, index: number) => void;
  handleOpenImageEdit: (index: number) => void;
  onImageLoad: (url: string, image: HTMLImageElement) => void;
};

// 效果图主列表项 memo 化：与参考图项同理，滚动期间未变项直接复用，
// 图片下载/收藏/发布等闭包在组件内部创建，不依赖父级每次渲染的新引用。
const OutputStackItem = memo(function OutputStackItem({
  index,
  image,
  previewImage,
  selected,
  inWindow,
  aspectRatio,
  failed,
  generating,
  progress,
  startedAt,
  refineOverlayVisible,
  refineStartedAt,
  showFailedRegenerate,
  favoriteSupported,
  publishSupported,
  allImages,
  itemRefs,
  onSelect,
  onPreview,
  onFavorite,
  onPublish,
  onRegenerateSingle,
  handleOpenRefine,
  handleOpenImageEdit,
  onImageLoad,
}: OutputStackItemProps) {
  const imageRef = useCallback((element: HTMLImageElement | null) => {
    if (element?.complete && element.naturalWidth > 0) onImageLoad(previewImage, element);
  }, [onImageLoad, previewImage]);
  return (
    <div
      ref={(element) => { itemRefs.current[index] = element; }}
      data-detail-replica-result-output-item={index + 1}
      aria-current={selected ? "true" : undefined}
      className="flex w-full items-start gap-2.5"
      // contain:layout paint 隔离该项布局与绘制：RefineOverlay 为图片盒内
      // absolute inset-0、序号角标也在图片盒内，paint 裁剪不会切掉溢出内容。
      style={{ contain: "layout paint" }}
    >
      <div
        data-detail-replica-result-output-image
        className="relative w-[400px] shrink-0"
        style={{ aspectRatio }}
      >
        {image && inWindow ? (
          <button
            type="button"
            data-detail-replica-result-image-frame
            onClick={() => {
              onSelect(index);
              onPreview?.(image, index);
            }}
            className="relative block w-full cursor-zoom-in border-0"
            aria-label={`预览第 ${index + 1} 屏效果图`}
          >
            <img
              key={previewImage}
              ref={imageRef}
              src={previewImage}
              alt={`第 ${index + 1} 屏效果图`}
              loading="lazy"
              decoding="async"
              onLoad={(event) => onImageLoad(previewImage, event.currentTarget)}
              className="block w-full h-auto"
            />
            <span className="absolute bottom-2 left-2 flex h-6 min-w-6 items-center justify-center rounded bg-black/70 px-1.5 text-[12px] text-white">
              {index + 1}
            </span>
          </button>
        ) : image ? (
          <div
            data-detail-replica-result-window-placeholder
            className="h-full w-full bg-[#111]"
          />
        ) : failed ? (
          <div className="flex h-full w-full flex-col items-center justify-center bg-[linear-gradient(135deg,#2D1B2E,#1A0A1E)] px-8 text-center">
            <span className="mb-3 flex h-9 w-9 items-center justify-center rounded-full border border-[#7A3E3D] bg-[#3A2120] text-[#ED6C5C]">×</span>
            <strong className="text-[13px] font-medium text-[#ED6C5C]">生成失败</strong>
            <p className="mt-2 text-[11px] leading-5 text-white/35">内容可能未通过校验，请调整后重新生成</p>
            {showFailedRegenerate && (
              <button
                type="button"
                onClick={() => onRegenerateSingle(index)}
                className="mt-4 rounded-[6px] bg-white/8 px-4 py-2 text-[12px] text-white/65 hover:bg-white/12 hover:text-white"
              >
                重新生成
              </button>
            )}
          </div>
        ) : generating ? (
          <div className="h-full w-full">
            <EcomGeneratingCard
              progress={progress}
              size="large"
              startedAt={startedAt}
              pauseWhenOffscreen
            />
          </div>
        ) : (
          <div className="flex h-full w-full items-center justify-center rounded-[2px] border border-[#2A2A2A] bg-[#111] text-sm text-white/35">
            等待生图
          </div>
        )}
        {refineOverlayVisible && <RefineOverlay visible={true} startedAt={refineStartedAt} />}
      </div>
      {image && inWindow ? (
        <div
          data-detail-replica-result-image-toolbar
          className="flex w-8 shrink-0 flex-col items-center gap-1.5 self-start pt-0"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <ImageActionToolbar
            size="lg"
            showBatchActions={allImages.length > 1}
            onSingleDownload={() => void downloadFile(image, `flowx_detail_${index + 1}.png`)}
            onDownloadAll={allImages.length > 1
              ? () => void downloadFiles(allImages, "flowx_detail_page")
              : undefined}
            onRegenerate={showFailedRegenerate ? () => onRegenerateSingle(index) : undefined}
            onFavorite={favoriteSupported ? () => onFavorite(image) : undefined}
            onPublish={publishSupported ? () => onPublish(image) : undefined}
            leading={(
              <>
                <ActionIconButton
                  label="AI重绘"
                  size="lg"
                  onClick={() => handleOpenRefine(image, index)}
                >
                  <RefineAiIcon className="h-4 w-4" />
                </ActionIconButton>
                <ActionIconButton
                  label="图像编辑"
                  size="lg"
                  onClick={() => handleOpenImageEdit(index)}
                >
                  <ImageEditActionIcon className="h-[18px] w-[18px]" />
                </ActionIconButton>
              </>
            )}
          />
        </div>
      ) : (
        <div className="w-8 shrink-0" aria-hidden="true" />
      )}
    </div>
  );
});

type FilmStripThumbnailItemProps = {
  index: number;
  url: string;
  failed: boolean;
  selected: boolean;
  width: number;
  height: number;
  onSelect: (index: number) => void;
};

// 每个缩略图项 memo 化：滑动/点击只改 activeIndex，只有失选和选中两个项的
// selected 会变，其余 16 个项的 props 完全一致，React 直接复用上次渲染结果，
// 不再重新创建/协调 18 个按钮子树，也不触发未变项的图片重绘。
// url/failed/width/height 在单次会话内稳定，onSelect 来自父级 useCallback，
// 因此 memo 比较只有 selected 会命中变化。
const FilmStripThumbnailItem = memo(function FilmStripThumbnailItem({
  index,
  url,
  failed,
  selected,
  width,
  height,
  onSelect,
}: FilmStripThumbnailItemProps) {
  return (
    <button
      key={`detail-replica-result-thumb-item-${index}`}
      type="button"
      data-detail-replica-result-thumbnail={index + 1}
      onClick={() => onSelect(index)}
      aria-current={selected ? "true" : undefined}
      aria-label={`定位到第 ${index + 1} 屏`}
      className={`group relative block shrink-0 cursor-pointer overflow-hidden border-0 p-0 ${
        failed && !url ? "bg-[#3A2120]" : "bg-transparent"
      }`}
      style={{
        width,
        height,
        // 缩略图栏一次性渲染 18 张全尺寸效果/参考图，滚动时持续解码重排会卡顿。
        // content-visibility:auto 让视口外的缩略图跳过内容渲染与图片解码，
        // containIntrinsicSize 锁定占位尺寸，避免跳过时布局塌陷。
        // 该栏仅通过点击 onSelect(index) 更新选中态，不依赖 offsetTop 追踪，
        // 因此跳过渲染不会影响滚动同步与选中态计算。
        contentVisibility: "auto",
        containIntrinsicSize: `${width}px ${height}px`,
      }}
    >
      {url ? (
        <img
          src={url}
          alt=""
          loading="lazy"
          decoding="async"
          className="block h-full w-full object-cover object-top"
        />
      ) : failed ? (
        <span className="flex h-full w-full items-center justify-center border border-[#7A3E3D] text-[14px] text-[#ED6C5C]">×</span>
      ) : (
        <span className="flex h-full w-full items-center justify-center bg-[#1A1A1A] text-[11px] text-white/25">
          {index + 1}
        </span>
      )}
      {!selected && (
        <span
          data-detail-replica-result-thumbnail-dim
          className="absolute inset-0 bg-black/55 transition-colors group-hover:bg-black/20"
        />
      )}
    </button>
  );
});

function FilmStripThumbnailRail({
  railRef,
  railAttr,
  count,
  activeIndex,
  urls,
  isFailedSlot,
  width,
  height,
  scale,
  onSelect,
}: {
  railRef: MutableRefObject<HTMLDivElement | null>;
  railAttr: "data-detail-replica-result-reference-thumb-rail" | "data-detail-replica-result-output-thumb-rail";
  count: number;
  activeIndex: number;
  urls: string[];
  isFailedSlot: (index: number) => boolean;
  width: number;
  height: number;
  scale: number;
  onSelect: (index: number) => void;
}) {
  const railProps = {
    [railAttr]: true,
    "data-detail-replica-result-thumbnail-rail": true,
  } as Record<string, boolean>;

  // isFailedSlot 是父级内联箭头函数，每次渲染引用都变，会让 memo 失效。
  // 预先算成布尔数组并 memo 化，子项只接收稳定的 failed: boolean，
  // 避免函数闭包破坏 memo 比较。
  const failedSlots = useMemo(
    () => Array.from({ length: count }, (_, index) => isFailedSlot(index)),
    [count, isFailedSlot],
  );

  return (
    <div
      ref={(node: HTMLDivElement | null) => { railRef.current = node; }}
      {...railProps}
      className="flex h-full w-[52px] shrink-0 items-end justify-center overflow-hidden"
    >
      <div
        data-detail-replica-result-thumbnail-stack
        data-detail-replica-thumbnail-scale={scale.toFixed(4)}
        className="flex max-h-full flex-col overflow-hidden rounded-[8px]"
        style={{ width }}
      >
        {Array.from({ length: count }, (_, index) => (
          <FilmStripThumbnailItem
            key={`detail-replica-result-thumb-${railAttr}-${index}`}
            index={index}
            url={urls[index] || ""}
            failed={failedSlots[index]}
            selected={activeIndex === index}
            width={width}
            height={height}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
}
